/**
 * createSessionAwareApiClient — Issue #289 + Issue #317.
 *
 * Drop-in replacement for `createApiClient` from `@jinzhuan/api-client` that
 * adds backend-authoritative session-idle-timeout detection. When a protected
 * call returns a 401 caused by idle expiration we fire `sessionExpirySignal`,
 * which `AuthContext` listens to in order to clear local auth state and
 * redirect to `/login`.
 *
 * Issue #317 — active-user false expiry fix:
 *   The JWT access TTL (default 15min) is shorter than the idle policy
 *   (default 30min). Without auto-refresh, an actively-working admin still
 *   sees `会话已失效` every 15 minutes because the JWT expires before the
 *   idle policy fires. We add a single-flight refresh-on-401 path that
 *   transparently rotates the access token via `/api/v1/auth/refresh` and
 *   replays the original request. Only a true idle-expiry response from
 *   `/auth/refresh` clears local auth — see `sessionRefresh.ts`.
 *
 * 红线:
 *   - The wrapper only inspects the response status / parsed body / thrown
 *     ApiError. It NEVER stores or forwards tokens, cookies, or refresh
 *     material.
 *   - Non-idle 401s (e.g. permission denied) are NOT treated as idle
 *     expiration; the message check is exact so legitimate auth errors
 *     surface as themselves.
 */
import { createApiClient, ApiError } from '@jinzhuan/api-client';
import type { CreateApiClientOptions } from '@jinzhuan/api-client';
import {
  isIdleExpiredResponseBody,
  notifySessionIdleExpired,
  parseAuthErrorBody,
} from '@/lib/sessionExpirySignal';
import { refreshAccessToken } from '@/lib/sessionRefresh';

/**
 * Minimal middleware shape — kept inline so `admin-web` doesn't take a direct
 * dependency on `openapi-fetch` internals. Matches the
 * `{ onResponse?(ctx): Promise<Response> | Response }` contract of the
 * openapi-fetch middleware that `createApiClient` already attaches.
 */
interface IdleDetectionMiddleware {
  onResponse(ctx: { response: Response }): Promise<Response> | Response;
}

/**
 * Inspect a 401 response body and, when it indicates idle expiration, fire
 * the global session-expiry signal. Returns the original error so callers
 * see a normal API error reaction (the auth-state cleanup happens in
 * AuthContext via the signal subscriber).
 */
export function reactToAuthError(err: unknown): unknown {
  if (err instanceof ApiError && err.status === 401) {
    const body = parseAuthErrorBody(err.bodyText);
    if (isIdleExpiredResponseBody(body)) {
      notifySessionIdleExpired('idle_expired');
    }
  }
  return err;
}

/**
 * Build a typed api-client whose 401 responses are inspected for backend
 * idle-timeout markers and transparently retried after rotating the access
 * token. Pass-through for everything else, so call-site code doesn't change
 * beyond swapping `createApiClient` for this helper.
 *
 * Middleware execution model (openapi-fetch v0.13):
 *   - `onResponse` runs in REVERSE registration order. We register the
 *     auto-refresh middleware LAST so it sees the raw 401 first; the
 *     existing idle-detection middleware sees the original response only if
 *     auto-refresh did not (or could not) replace it.
 */
export function createSessionAwareApiClient(opts: CreateApiClientOptions): ReturnType<typeof createApiClient> {
  const client = createApiClient(opts);
  const idleDetectionMiddleware: IdleDetectionMiddleware = {
    async onResponse({ response }) {
      // Only inspect 401s — let the existing error envelope middleware do its
      // job for every other status. We MUST clone the body because the
      // downstream consumer still needs to read it.
      if (response.status !== 401) {
        return response;
      }
      const ct = response.headers.get('content-type') ?? '';
      if (!ct.includes('application/json')) {
        return response;
      }
      try {
        const cloned = response.clone();
        const text = await cloned.text();
        const body = parseAuthErrorBody(text);
        if (isIdleExpiredResponseBody(body)) {
          notifySessionIdleExpired('idle_expired');
        }
      } catch {
        // Swallow read/parse failures — a malformed 401 body is not a signal.
      }
      return response;
    },
  };
  client.use(idleDetectionMiddleware);
  client.use(buildAutoRefreshMiddleware(opts));
  return client;
}

/**
 * Build the auto-refresh middleware. Kept exported so unit tests can drive it
 * directly without spinning up the full api-client.
 */
export function buildAutoRefreshMiddleware(opts: CreateApiClientOptions): AutoRefreshMiddleware {
  // Map of request-id -> cloned Request captured at onRequest time. We clone
  // BEFORE fetch consumes the body so the replay request still has a fresh
  // body stream. The clone has the Bearer header from the auth middleware
  // because onRequest middlewares run in FORWARD order (auth first, refresh
  // last).
  const captured = new Map<string, Request>();
  return {
    onRequest({ request, id }) {
      // request.clone() tees the body stream so the original Request stays
      // intact for fetch. We do not modify the request here.
      try {
        captured.set(id, request.clone());
      } catch {
        // If the runtime refuses to clone (very old polyfills) we simply skip
        // the auto-refresh path — the existing 401 propagates as before.
      }
    },
    onError({ id }) {
      // Free the captured request when the underlying fetch rejected — we
      // never reach onResponse so the finally-block cleanup does not run.
      captured.delete(id);
    },
    async onResponse({ response, id }) {
      try {
        if (response.status !== 401) {
          return undefined;
        }
        const ct = response.headers.get('content-type') ?? '';
        let bodyText: string | null = null;
        if (ct.includes('application/json')) {
          try {
            bodyText = await response.clone().text();
          } catch {
            // Treat unreadable bodies as "unknown 401" — do not refresh.
            return undefined;
          }
        }
        const body = parseAuthErrorBody(bodyText);
        if (isIdleExpiredResponseBody(body)) {
          // Backend says the session itself is idle-expired. The
          // idle-detection middleware (registered earlier so it runs LATER
          // in the reverse onResponse chain) will fire `sessionExpirySignal`.
          // Do NOT attempt refresh — it would just be rejected again with
          // the same idle body.
          return undefined;
        }
        // Generic 401 — likely the JWT access token expired but the
        // refresh-token row is still alive. Try to rotate and replay.
        const stashed = captured.get(id);
        if (!stashed) {
          return undefined;
        }
        // Only refresh for protected endpoints. Public endpoints (e.g.
        // /auth/login, /auth/refresh itself) never send Authorization, so an
        // anonymous 401 means "credentials wrong" — not "JWT expired".
        if (!stashed.headers.get('Authorization')) {
          return undefined;
        }
        const outcome = await refreshAccessToken({
          baseUrl: opts.baseUrl,
          fetch: opts.fetch,
        });
        if (outcome.status === 'idle_expired') {
          // Refresh was rejected because the session is genuinely idle-expired.
          // Surface the signal so AuthContext clears local auth state, then
          // let the original 401 reach the caller.
          notifySessionIdleExpired('idle_expired');
          return undefined;
        }
        if (outcome.status !== 'refreshed') {
          return undefined;
        }
        const replayed = await replayWithBearer(stashed, outcome.accessToken, opts.fetch);
        return replayed ?? undefined;
      } finally {
        captured.delete(id);
      }
    },
  };
}

/**
 * Re-send the original Request with a freshly-rotated bearer token. Returns
 * `null` when the runtime cannot reissue the request — the original 401
 * response then propagates as before, which is the safest fallback.
 *
 * For unsafe methods (POST/PUT/PATCH/DELETE) we MUST recompute the
 * `X-CSRF-Token` header from `document.cookie` because `/api/v1/auth/refresh`
 * rotates the non-HttpOnly `jzc_csrf` cookie. The captured pre-refresh
 * header would otherwise be rejected by the backend `CsrfGuard`.
 */
async function replayWithBearer(
  stashed: Request,
  accessToken: string,
  fetchImpl?: typeof globalThis.fetch,
): Promise<Response | null> {
  const fetchFn = fetchImpl ?? globalThis.fetch;
  if (typeof fetchFn !== 'function') return null;
  try {
    const headers = new Headers(stashed.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);
    const method = stashed.method.toUpperCase();
    const isUnsafeMethod =
      method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    if (isUnsafeMethod) {
      // `/auth/refresh` rotates jzc_csrf. The original request carried the
      // pre-refresh value; reread document.cookie so the replay matches the
      // freshly-rotated CSRF cookie.
      const rotatedCsrf = readJzcCsrfCookie();
      if (rotatedCsrf) {
        headers.set('X-CSRF-Token', rotatedCsrf);
      } else {
        // No cookie available (test/SSR environment); strip the stale value
        // rather than send a guaranteed mismatch.
        headers.delete('X-CSRF-Token');
      }
    }
    const hasBody = method !== 'GET' && method !== 'HEAD';
    // We use the cloned Request's body stream. `new Request(stashed, { headers })`
    // would also work but some polyfills mishandle credentials so we
    // reconstruct explicitly.
    const body = hasBody ? await stashed.clone().arrayBuffer() : undefined;
    const init: RequestInit = {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body } : {}),
    };
    return await fetchFn(stashed.url, init);
  } catch {
    return null;
  }
}

/**
 * Read the non-HttpOnly `jzc_csrf` cookie. Returns `undefined` when
 * `document` is unavailable (Node test envs, SSR) or when the cookie has
 * not been set yet.
 *
 * 红线: the cookie value is treated as opaque — we never log it.
 */
function readJzcCsrfCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)jzc_csrf=([^;]+)/);
  return match ? match[1] : undefined;
}

interface AutoRefreshMiddleware {
  onRequest(ctx: { request: Request; id: string }): void;
  onError(ctx: { error: unknown; id: string }): void;
  onResponse(ctx: { response: Response; id: string }): Promise<Response | undefined>;
}
